import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getHintForPage } from '../utils/hintEngine';
import dashboardService from '../services/dashboardService';
import { useLocation } from 'react-router-dom';

/**
 * Hook para obter dica contextual para a página atual
 * 
 * @param {string} pageName - Nome da página (dashboard, professionals, services, places, schedules)
 * @param {object} pageData - Dados do escopo da página (stats, items, etc.)
 * @returns {object|null} - Objeto hint { type, message, action } ou null
 * 
 * @example
 * // Em DashboardPage
 * const hint = useContextualHint('dashboard', { stats });
 * 
 * @example
 * // Em ProfessionalsPage
 * const hint = useContextualHint('professionals', { items: professionals });
 * 
 * @example
 * // Em PlacesPage (normalizado)
 * const hint = useContextualHint('places', { places, data: normalizedData });
 */
export const useContextualHint = (pageName, pageData) => {
  const location = useLocation();

  // Seed que muda a cada troca de rota (menu)
  const [navSeed, setNavSeed] = useState(() => {
    const stored = sessionStorage.getItem('hint_nav_seed');
    const parsed = stored ? Number(stored) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  });

  useEffect(() => {
    const next = navSeed + 1;
    setNavSeed(next);
    sessionStorage.setItem('hint_nav_seed', String(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const hasStats = !!pageData?.stats;

  const statsQuery = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => dashboardService.getStats(),
    enabled: !hasStats,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const mergedData = useMemo(() => {
    const globalStats = statsQuery.data;
    const stats = pageData?.stats || globalStats;

    return {
      ...(pageData || {}),
      stats,
      globalStats,
      routePath: location.pathname,
      navSeed,
    };
  }, [pageData, statsQuery.data, location.pathname, navSeed]);

  const hint = useMemo(() => {
    return getHintForPage(pageName, mergedData);
  }, [pageName, mergedData]);

  return hint;
};
